import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  Users, 
  Shield, 
  Share2, 
  Zap, 
  Settings,
  Phone,
  Home,
  Building,
  Star,
  Play,
  Clock,
  Bell,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  Crown,
  Sparkles,
  Heart,
  MessageCircle,
  Calendar,
  CheckCircle
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import SynchronizedAnimations from './SynchronizedAnimations';
import AppSyncAnimation from './AppSyncAnimation';
import DashboardAnimation from './DashboardAnimation';
import RoomStatusAnimation from './RoomStatusAnimation';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [demoRoomState, setDemoRoomState] = useState('available');
  const [currentFeature, setCurrentFeature] = useState(0);

  // Demo room state cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setDemoRoomState((prev) => {
        if (prev === 'available') return 'occupied';
        if (prev === 'occupied') return 'maintenance';
        return 'available';
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Feature cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 3);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const getDemoStateColor = (state: string) => {
    switch (state) {
      case 'available': return '#22c55e';
      case 'occupied': return '#ef4444';
      case 'maintenance': return '#f59e0b';
      default: return '#22c55e';
    }
  };

  const getDemoStateEmoji = (state: string) => {
    switch (state) {
      case 'available': return '🟢';
      case 'occupied': return '🔴';
      case 'maintenance': return '🟡';
      default: return '🟢';
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/rooms');
    } else {
      navigate('/register');
    }
  };

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Role-Based Access',
      description: 'Control who can view or change your rooms',
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: 'Public Links',
      description: 'Share room states with anyone via secure links',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Real-time Updates',
      description: 'Instant synchronization across all devices',
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Custom States',
      description: 'Define your own room states with colors and emojis',
    },
  ];

  const useCases = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Roommates',
      description: 'Signal when someone is over or you need privacy',
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: 'Call Availability',
      description: 'Let friends know when you\'re available for calls',
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: 'House Status',
      description: 'Coordinate household activities and presence',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Roommate',
      company: 'Shared House',
      content: 'RoomState transformed how we coordinate at home. I can signal when someone\'s over without texting everyone.',
      avatar: 'SC',
    },
    {
      name: 'Mike Rodriguez',
      role: 'Remote Worker',
      company: 'Tech Team',
      content: 'Perfect for letting friends know when I\'m available for calls without being rude or unavailable in Slack.',
      avatar: 'MR',
    },
  ];

  const features2 = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Timed States',
      description: 'Set automatic timeouts for your room states',
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: 'Notifications',
      description: 'Get notified when room states change',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Public Links',
      description: 'Share read-only room states with anyone',
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Mobile App',
      description: 'Update rooms from anywhere on your phone',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">


      {/* Hero Section */}
      <section className="relative px-6 py-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-green-400/10 to-blue-400/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>Simple. Flexible. Human.</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 dark:text-white leading-tight">
                  Digital Rooms for
                  <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Real Life
                  </span>
                </h1>
                
                <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
                  Create digital rooms to communicate your availability, presence, or situation without 
                  interrupting what you're doing. Perfect for roommates, friends, and coworkers.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleGetStarted}
                  className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                  <span className="flex items-center gap-2">
                    {isAuthenticated ? 'Go to Rooms' : 'Start Free Trial'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-0 group-hover:opacity-20 transition-opacity" />
                </button>
                
              </div>

              <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">S</div>
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">M</div>
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium">A</div>
                  </div>
                  <span>Join 1,000+ users</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span>4.9/5 rating</span>
                </div>
              </div>
            </div>

            {/* Right Column - Demo */}
            <div className="relative">
              <div className="relative bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700/50 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/20 dark:to-purple-900/20" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold">🏠</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white">House</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">3 roommates</p>
                      </div>
                    </div>
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  </div>

                  <div className="space-y-4">
                    <div 
                      className="p-4 rounded-xl transition-all duration-500 hover:scale-105 cursor-pointer"
                      style={{ 
                        backgroundColor: getDemoStateColor(demoRoomState),
                        boxShadow: `0 8px 32px ${getDemoStateColor(demoRoomState)}40`
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getDemoStateEmoji(demoRoomState)}</span>
                        <div>
                          <div className="font-semibold text-white capitalize">{demoRoomState}</div>
                          <div className="text-white/80 text-sm">Updated 2 min ago</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">Call Availability</div>
                        <div className="text-green-600 text-sm">Available</div>
                      </div>
                      <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">Birthday Mode</div>
                        <div className="text-red-600 text-sm">Busy</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-bounce" />
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Everything you need to
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                stay in sync
              </span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              RoomState gives you the tools to communicate your availability and presence 
              in a way that feels natural and doesn't interrupt your flow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 bg-gradient-to-br from-slate-50 to-white dark:from-slate-700 dark:to-slate-800 rounded-2xl border border-slate-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Perfect for any situation
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Whether you're living with roommates, working remotely, or just want to 
              communicate your availability without being rude.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="group relative p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                    {useCase.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 dark:text-white mb-3">
                    {useCase.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animation Section */}
      <section className="px-6 py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              See it in action
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Watch how RoomState keeps everyone synchronized in real-time across all devices.
            </p>
          </div>

          <AppSyncAnimation />
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Customizable Dashboard
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Create your personalized room monitoring setup with resizable widgets and multiple display modes.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Feature Cards - Left Side */}
            <div className="w-full lg:w-1/3 space-y-4">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Resizable Widgets</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Customize the size of each room widget to fit your needs
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Multiple Views</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Choose between full details, compact view, or simple emoji indicators
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Real-time Updates</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Instantly update room status and see changes across all widgets
                </p>
              </div>
            </div>

            {/* Animation */}
            <div className="w-full lg:w-2/3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-3xl" />
                <div className="relative p-8 md:p-12">
                  <DashboardAnimation />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/dashboards')}
              className="group relative inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl hover:from-blue-700 hover:to-purple-800 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 border-2 border-white/20"
            >
              <Settings className="w-6 h-6" />
              <span className="relative">Create your dashboard</span>
            </button>
          </div>
        </div>
      </section>

      {/* API Integration Section */}
      <section className="px-6 py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Powerful API Integration
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Integrate room status updates directly into your applications with our robust API.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Feature Text */}
            <div className="w-full lg:w-1/3 space-y-8">
              <div className="p-6 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                    <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">Developer-Friendly</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400">
                  Simple REST API with comprehensive documentation and SDKs for popular languages.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30">
                    <Zap className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">Real-time Updates</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400">
                  Webhook support and real-time event streaming for instant status updates.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                    <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">Enterprise Ready</h3>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Unlimited rooms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Maximum 200 participants per room</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Unlimited broadcast links</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Advanced analytics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Dedicated support</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Animation */}
            <div className="w-full lg:w-2/3">
              <SynchronizedAnimations />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Loved by people like you
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              See what our users say about how RoomState has improved their daily coordination.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="group p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {testimonial.role} at {testimonial.company}
                    </div>
                  </div>
                </div>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-6 py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Start free and upgrade when you need more features. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="relative p-8 bg-gradient-to-br from-slate-50 to-white dark:from-slate-700 dark:to-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-600">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Free</h3>
                <div className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  €0
                  <span className="text-lg text-slate-500 dark:text-slate-400 font-normal">/month</span>
                </div>
                <p className="text-slate-600 dark:text-slate-400">Perfect for getting started</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Up to 10 rooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">10 participants per room</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Custom room states</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">1 broadcast link per room</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Real-time updates</span>
                </div>
              </div>

              <button
                onClick={handleGetStarted}
                className="w-full py-3 px-6 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold rounded-xl hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
              >
                Get Started Free
              </button>
            </div>

            {/* Pro Plan */}
            <div className="relative p-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl border-2 border-blue-500 shadow-xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </div>
              </div>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Pro</h3>
                <div className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  €4
                  <span className="text-lg text-slate-500 dark:text-slate-400 font-normal">/month</span>
                </div>
                <p className="text-slate-600 dark:text-slate-400">For power users and teams</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Unlimited rooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">50 participants per room</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">5 broadcast links per room</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Priority support</span>
                </div>
              </div>

              <button
                onClick={handleGetStarted}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Start Pro Trial
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="relative p-8 bg-gradient-to-br from-slate-50 to-white dark:from-slate-700 dark:to-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-600">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Enterprise</h3>
                <div className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  €6
                  <span className="text-lg text-slate-500 dark:text-slate-400 font-normal">/month</span>
                </div>
                <p className="text-slate-600 dark:text-slate-400">For large organizations</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Unlimited rooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">200 participants per room</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Unlimited broadcast links</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Advanced analytics</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Dedicated support</span>
                </div>
              </div>

              <button
                onClick={handleGetStarted}
                className="w-full py-3 px-6 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold rounded-xl hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative px-6 py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 via-purple-600/90 to-pink-600/90" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to create your digital rooms?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of people already using RoomState to communicate their availability and presence in a more human way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="group relative px-8 py-4 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              <span className="flex items-center gap-2">
                {isAuthenticated ? 'Go to Rooms' : 'Start Your Free Trial'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;